import { useEffect, useContext, useState, useReducer } from 'react'
import styled from '@emotion/styled'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { extractDataTypes } from '@global/constants'
import { initialState, splitReducer, Calculated, SplitActionTypes } from './split-actions'
import { CvsAutoComplete } from '../components'
import { fieldSetContext } from '../contexts'
import { SplitPreview } from './previewComponent/split-preview'
import { t } from '@lingui/core/macro'

const FillAutocompleteExtractData = styled(CvsAutoComplete as any)`
  width: 100%;
`

const FillAutocompleteExtractDataFrom = styled(CvsAutoComplete as any)`
  width: 100%;
`

const FillAutocompleteDelimiter = styled(CvsAutoComplete as any)`
  width: 100%;
`
const CustomDelimiterField = styled(TextField)`
  width: 100%;
`

const FlexBox = styled(Box)`
  display: flex;
  margin-top: 8px;
  margin-bottom: 4px;
  gap: 8px;
  justify-content: space-between;
`

const PositionTextField = styled(TextField)`
  width: 100%;
`

type CalculatedWithFun = Calculated & { function: string }

interface Form {
  fieldset_id: string
  field_id: string
  id: string
  transformation?: {
    calculated: CalculatedWithFun[]
    allow_reprocessing: boolean
  }
}

interface SplitTextProps {
  form: Form
  setForm: React.Dispatch<React.SetStateAction<Form | undefined>>
  disabled: boolean
}

export const SplitText = ({ form, setForm, disabled }: SplitTextProps) => {
  const { byId } = useContext(fieldSetContext)

  const [showTransformOptions, setShowTransformOptions] = useState(false)
  const [selectedTransformationType, setSelectedTransformationType] = useState('')

  // Split state
  const [splitState, splitDispatch] = useReducer(splitReducer, initialState)
  const [isCustomDelimiter, setIsCustomDelimiter] = useState(false)
  const [customDelimiterValue, setCustomDelimiterValue] = useState<string | null>(null)

  const positionToOffsetArg = (position: string) => {
    return (Number(position) - 1).toString() || '0'
  }

  const offsetArgToPosition = (offset: string) => {
    return (Number(offset) + 1).toString() || '1'
  }

  const DELIMITER_OPTIONS = [
    { label: t` - (dash)`, value: '-' },
    { label: t` _ (underscore)`, value: '_' },
    { label: t`  (space)`, value: ' ' },
    { label: t` | (pipe)`, value: '|' },
    { label: t`Custom`, value: customDelimiterValue },
  ]

  useEffect(() => {
    const fieldSetId = form.fieldset_id
    if (!fieldSetId) {
      splitDispatch({
        type: SplitActionTypes.setSelectableFields,
        payload: {
          selectableFields: byId[fieldSetId]?.fields.map(field => ({
            id: field.id,
            label: field.name,
            value: field.id,
            governance_type: field.governance_type,
          })),
        },
      })
    } else {
      const currentSelectedId = form.id
      splitDispatch({
        type: SplitActionTypes.setSelectableFields,
        payload: {
          selectableFields: byId[fieldSetId]?.fields
            .filter(field => field.id !== currentSelectedId)
            .map(field => ({
              id: field.id,
              label: field.name,
              value: field.id,
              governance_type: field.governance_type,
            })),
        },
      })
    }

    const [calculated] = form.transformation?.calculated ?? []

    if (calculated) {
      const transformationType = calculated.func ?? calculated.function

      if (transformationType === 'split') {
        setSelectedTransformationType('split')

        splitDispatch({
          type: SplitActionTypes.setExistingSplitTransform,
          payload: {
            originFieldId: calculated.origin_field_id,
            delimiter: calculated.arguments[0],
            position: offsetArgToPosition(calculated.arguments[1]),
            allowReprocessing: calculated.allow_reprocessing,
            allowReprocessingOnlyForInbound: calculated.allow_reprocessing_only_for_inbound,
          },
        })
      }

      setShowTransformOptions(true)
    }
  }, [byId])

  useEffect(() => {
    if (!['-', '_', ' ', '|'].includes(String(splitState?.delimiter))) {
      setCustomDelimiterValue(String(splitState?.delimiter))
      setIsCustomDelimiter(true)
    } else {
      setIsCustomDelimiter(false)
    }
  }, [splitState])

  function updateForm(
    field:
      | 'type'
      | 'delimiter'
      | 'position'
      | 'originField'
      | 'allowRegeneration'
      | 'allowReprocessingOnlyForInbound',
    value: string | boolean,
  ): void {
    setForm(prev => {
      const currentForm = prev ?? form

      // This we will use to track the current transform data
      const current: CalculatedWithFun[] = [...(currentForm.transformation?.calculated ?? [])]

      // User is removing the transform
      if (value === 'NONE') {
        splitDispatch({
          type: SplitActionTypes.reset,
          payload: {},
        })

        return {
          ...prev,
          transformation: {
            ...currentForm.transformation,
            calculated: [],
          },
        } as Form
      }

      let calculatedObj: Calculated = {
        arguments: ['', ''],
        transformation_id: '',
        origin_field_id: '',
        func: selectedTransformationType,
        origin_field_type: 'field',
        allow_reprocessing: false,
        allow_reprocessing_only_for_inbound: false,
      }

      if (current[0]) {
        // For now we only have one transform option
        calculatedObj = { ...current[0] }
      }

      switch (field) {
        case 'type':
          calculatedObj.func = value as string
          break
        case 'delimiter':
          calculatedObj.arguments[0] = value as string
          break
        case 'position':
          calculatedObj.arguments[1] = positionToOffsetArg(value as string)
          break
        case 'originField':
          calculatedObj.origin_field_id = value as string
          break
        case 'allowRegeneration':
          calculatedObj.allow_reprocessing = value as boolean
          break
        case 'allowReprocessingOnlyForInbound':
          calculatedObj.allow_reprocessing_only_for_inbound = value as boolean
          break
        default:
          break
      }

      return {
        ...prev,
        transformation: {
          ...currentForm.transformation,
          calculated: [calculatedObj],
        },
      } as Form
    })
  }

  return (
    <>
      <Box>
        <FillAutocompleteExtractData
          name="extract-data"
          margin="dense"
          disabled={disabled}
          label={t`Extract data`}
          options={Object.keys(extractDataTypes).map(key => ({
            label: extractDataTypes[key],
            value: key,
          }))}
          onSelected={(value: string) => {
            if (value !== 'NONE') {
              setShowTransformOptions(true)
            } else {
              setShowTransformOptions(false)
            }
            setSelectedTransformationType(value)
            splitDispatch({
              type: SplitActionTypes.setOriginFieldId,
              payload: { originFieldId: '' },
            })
            updateForm('type', value)
          }}
          selected={selectedTransformationType || 'NONE'}
        />
      </Box>
      {showTransformOptions ? (
        selectedTransformationType === 'split' ? (
          <>
            {/* Split - Text */}
            <Box display="flex" flexWrap="wrap">
              <FillAutocompleteExtractDataFrom
                name="extract-data-from"
                margin="dense"
                disabled={disabled}
                label={t`Extract data from`}
                placeholder={t`Field selection`}
                selected={splitState?.originFieldId}
                options={splitState?.selectableFields?.filter(sf => sf.id !== form.field_id)}
                onSelected={(fieldId: string) => {
                  splitDispatch({
                    type: SplitActionTypes.setOriginFieldId,
                    payload: { originFieldId: fieldId },
                  })
                  updateForm('originField', fieldId)
                }}
              />
            </Box>
            <Box display="flex" justify-content="space-between">
              <FillAutocompleteDelimiter
                name="delimiter"
                margin="dense"
                variant="outlined"
                disabled={disabled}
                label={t`Delimiter`}
                required
                placeholder={t`Delimiter`}
                options={DELIMITER_OPTIONS}
                onChange={({ value }) => {
                  splitDispatch({
                    type: SplitActionTypes.setDelimiter,
                    payload: { delimiter: value },
                  })
                  updateForm('delimiter', value)
                  setIsCustomDelimiter(value == customDelimiterValue)
                }}
                selected={splitState?.delimiter}
                data-test-page-id="delimiter-dropdown"
              />
            </Box>
            <FlexBox>
              <CustomDelimiterField
                name="custom-delimiter-value"
                variant="outlined"
                disabled={disabled || !isCustomDelimiter}
                label={t`Custom delimiter value`}
                defaultValue={splitState?.delimiter}
                size="small"
                {...(isCustomDelimiter && { required: true })}
                onBlur={({ target: { value } }: { target: { value: string } }) => {
                  splitDispatch({
                    type: SplitActionTypes.setDelimiter,
                    payload: { delimiter: value },
                  })
                  if (!['-', '_', ' ', '|'].includes(value)) {
                    setIsCustomDelimiter(true)
                    setCustomDelimiterValue(value)
                  } else {
                    setIsCustomDelimiter(false)
                    setCustomDelimiterValue(null)
                  }
                  updateForm('delimiter', value)
                }}
              />
              <PositionTextField
                name="data-position"
                variant="outlined"
                type="number"
                disabled={disabled}
                label={t`Position`}
                value={splitState?.position}
                size="small"
                inputProps={{ min: 1 }}
                onChange={({ target: { value } }: { target: { value: string } }) => {
                  const formattedInput = /[0-9]*/.exec(value)?.[0]
                  if (typeof formattedInput === 'undefined') {
                    return
                  }
                  splitDispatch({
                    type: SplitActionTypes.setPosition,
                    payload: { position: formattedInput },
                  })
                  updateForm('position', formattedInput)
                }}
              />
            </FlexBox>
            <Box mt={1}>
              <Tooltip title={t`Allow re-processing of this field's value when re-submitting.`}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitState?.allowReprocessing}
                        onChange={({ target: { checked } }: { target: { checked: boolean } }) => {
                          const allowReprocessingOnlyForInbound =
                            splitState?.allowReprocessingOnlyForInbound && checked
                              ? !checked
                              : splitState?.allowReprocessingOnlyForInbound
                          splitDispatch({
                            type: SplitActionTypes.setAllowReprocessing,
                            payload: {
                              allowReprocessing: checked,
                              allowReprocessingOnlyForInbound,
                            },
                          })
                          updateForm('allowRegeneration', checked)
                          updateForm(
                            'allowReprocessingOnlyForInbound',
                            allowReprocessingOnlyForInbound,
                          )
                        }}
                        disabled={disabled}
                      />
                    }
                    label={t`Allow reprocessing`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitState?.allowReprocessingOnlyForInbound}
                        onChange={({ target: { checked } }: { target: { checked: boolean } }) => {
                          const allowReprocessing =
                            splitState?.allowReprocessing && checked
                              ? !checked
                              : splitState?.allowReprocessing
                          splitDispatch({
                            type: SplitActionTypes.setAllowReprocessingOnlyForInbound,
                            payload: {
                              allowReprocessingOnlyForInbound: checked,
                              allowReprocessing,
                            },
                          })
                          updateForm('allowReprocessingOnlyForInbound', checked)
                          updateForm('allowRegeneration', allowReprocessing)
                        }}
                        disabled={disabled}
                      />
                    }
                    label={t`Allow Reprocessing Only For Inbound`}
                  />
                </Box>
              </Tooltip>
            </Box>
            <SplitPreview
              delimiter={splitState?.delimiter}
              position={positionToOffsetArg(splitState?.position)}
              shouldRender={!disabled}
            />
          </>
        ) : null
      ) : null}
    </>
  )
}
